import {
  useMemo,
  useState,
  Fragment,
  useEffect,
} from 'react';
import type {
  ChangeEvent,
} from 'react';
import {
  usePageContext,
} from 'vike-react/usePageContext';
import {
  useAlerts,
  useAuthentication,
  useEmails,
} from '@dinosave/core/stores';
import {
  validateEmail,
} from '@dinosave/core/validations';
import {
  Modal,
} from '@/components/Modals';
import type {
  ModalProps,
} from '@/components/Modals';
import Input from '@/ui/components/Input';
import Text from '@/ui/components/Text';

import styles from './TestingAccessModal.module.css';

const TestingAccessModal = (props: ModalProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    isOpen: propsIsOpen,
    onClose,
    ...rest
  } = props;

  // ##########################################################################
  // PARAMS
  // ##########################################################################

  const {
    urlParsed,
  } = usePageContext();
  const {
    search,
  } = urlParsed;
  const isAndroid = search['android'] === 'true';
  const defaultIsOpen = isAndroid || propsIsOpen;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const addError = useAlerts(s => s.addError);

  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserEmail = authenticatedUser?.email;

  const createEmail = useEmails(s => s.createEmail);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [email, setEmail] = useState(authenticatedUserEmail);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isOpen, setOpen] = useState(defaultIsOpen);

  // ##########################################################################
  // MEMOIZATION
  // ##########################################################################

  const isEmailValid = useMemo(() => (
    validateEmail(email)
  ), [email]);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmailInvalid(false);
    setEmail(e.target.value);
  };

  const onSubmit = async () => {
    if (
      loading
    ) return;

    if (
      !isEmailValid
    ) return setEmailInvalid(true);

    setLoading(true);

    try {
      await createEmail(email);
      setSuccess(true);
    } catch (e) {
      setLoading(false);
      addError(e);
    }
  };

  const handleClose = () => {
    onClose();
    setOpen(false);
  };

  const close = success && handleClose;
  const handleSubmit = !success && onSubmit;

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    if (
      isAndroid
    ) return;

    setOpen(propsIsOpen);
  }, [propsIsOpen]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Modal
      {...rest}
      isOpen={isOpen}
      title="Hooray!"
      cancelText="Close"
      confirmText="Request Access"
      onClose={handleClose}
      onCancel={close}
      onConfirm={handleSubmit}
      className={styles.testingAccessModal}
      contentClassName={styles.content}
    >
      <div className={styles.info}>
        {success ? (
          <Text
            kind="paragraph"
            size="regular"
            color="neutral-500"
            className={styles.success}
          >
            We’ll review your request and will send you an email with a Google Play link.
            Thanks for participating!
          </Text>
        ) : (
          <Fragment>
            <Text
              kind="paragraph"
              size="regular"
              color="neutral-500"
              className={styles.description}
            >
              Dinosave on Android is currently in closed beta, and we’re giving early users exclusive access plus a free Premium subscription.
            </Text>

            <Input
              value={email}
              onChange={handleEmailChange}
              invalid={emailInvalid}
              placeholder="Enter your Email"
              disabled={loading}
            />

            <Text
              kind="paragraph"
              size="regular"
              color="neutral-500"
              className={styles.hint}
            >
              Provide your Google Play Store account email above and we’ll send you an invite. Thanks! 🤗
            </Text>
          </Fragment>
        )}
      </div>
    </Modal>
  );
};

export default TestingAccessModal;
